import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, type HTMLAttributes, useContext, useState } from 'react';
import { type RequiredPick } from '../../../../../@types/build';
import { type ProductOptionGroupFieldsFragment, type ProductOptionFieldsFragment } from '../../../__generated__/graphql-client-types';
import { ProductFamilyContext } from '../../../contexts/product-family/product-family.context';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { AnalyticsHelper } from '../../../helpers/analytics/analytics.helper';
import { formatPrice } from '../../../helpers/general-helper/general-helper';
import { changeOption } from '../../../helpers/product-helper/configuration/product-change-option.helper';
import { byId } from '../../../helpers/product-helper/configuration/product-configuration.helper';
import {
	areSelectionsAvailable,
	changeGroupValidity,
	generateProductOptionNameSelector,
	handleProductOptionClick,
	isCurrentOptionSelected,
	isOptionSelected
} from '../../../helpers/product-helper/configuration/product-option.helper';
import {
	getGASelectConfigurationNestedOption,
	getGASelectConfigurationOption,
	getGASelectVariation,
	type SelectedNestedProductOption
} from '../../../helpers/product-helper/product-analytics.helper';
import { useClientOnlyBreakpoints } from '../../../hooks/breakpoints/client-only-breakpoints.hook';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import {
	type GroupSelection,
	OptionSelectionType,
	type OptionSelection,
	type ProductOptionSelection
} from '../../../types/product-option.types';
import { StyledButton } from '../../buttons';
import { Drawer, type DrawerProps } from '../../common-components/drawer/drawer.component';
import { DropdownMenu, DropdownMenuSelect, DROPDOWN_CLASSES } from '../../common-components/dropdown-menu/dropdown-menu.component';
import ChevronRightIcon from '../../svgs/untitled-ui/chevron-right.svg';
import { OptionPill } from './option-pill/option-pill.component';
import { OptionText } from './option-text/option-text.component';
import { productOption, productGroupOption } from './product-option.css';

export const useTrackConfigurationOptionChange = (): ((
	group: ProductOptionGroupFieldsFragment,
	option?: ProductOptionFieldsFragment,
	options?: SelectedNestedProductOption
) => Promise<any>) => {
	const { productFamily } = useContext(ProductFamilyContext);
	const { selectedVariant } = useContext(VariantContext);
	return (group, option, options): Promise<any> => {
		const info = { product: productFamily, variant: selectedVariant };
		if (info.product && info.variant) {
			if (group.type === 'VARIATION' && option) {
				return AnalyticsHelper.track(getGASelectVariation(option.name, info).data);
			} else if (group.type === 'REQUIRED' && group.selectionType === 'INLINE' && option) {
				return AnalyticsHelper.track(getGASelectConfigurationOption(option.name, info, option.value).data);
			} else if (group.selectionType === 'NESTED' && options) {
				return AnalyticsHelper.track(getGASelectConfigurationNestedOption(options, info).data);
			}
		}
		return Promise.resolve();
	};
};

export const formatSelectedLabel = (option: OptionSelection): string | JSX.Element => {
	return (
		<div className="dib">
			<span>{option.name}</span>
			{option.price > 0 ? <span className="theme-purchase ml2">- {formatPrice(option.price)}</span> : null}
		</div>
	);
};

export const getSelectedLabel = (
	label: string,
	selections?: OptionSelection[],
	group?: ProductOptionGroupFieldsFragment
): string | JSX.Element => {
	if (selections && areSelectionsAvailable(selections)) {
		const currentOptionsSelected = selections.filter((option) => isOptionSelected(option));
		if (currentOptionsSelected.length === 0) {
			return label;
		}
		return currentOptionsSelected.length > 1
			? `(${currentOptionsSelected.length}) Options Selected`
			: formatSelectedLabel(currentOptionsSelected[0]);
	} else if (group) {
		const optionSelected = group.options.find((option) => option.selected);
		if (optionSelected) {
			return optionSelected.name;
		}
	}
	return label;
};

/**
 * @typedef ProductOptionProps
 */
export type ProductOptionProps = {
	group: ProductOptionGroupFieldsFragment;
	groupPosition: number;
	showCheckmark?: boolean;
} & ProductOptionFieldsFragment &
	Pick<ProductOptionsProps, 'change' | 'selections'> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Option Component
 */
export const ProductOption: FunctionComponent<ProductOptionProps> = (props) => {
	const { className, change, showCheckmark, group, selections, groupPosition, ...option } = props;
	const trackChangeOption = useTrackConfigurationOptionChange();
	const navigate = useNavigation();
	const groupId = kebabCase(group.label);
	const isTextField = option.type === 'TEXTBOX';
	const selectedClassName = option.selected ? 'b--theme-grey-darker' : 'b--theme-grey-light';
	const optionSelectionFound = (selections || []).find((optionSelection) =>
		byId<{ id: string }>(optionSelection, { id: kebabCase(option.name) })
	);
	const onClick = () => handleProductOptionClick(option, group, navigate, onChange);
	const onChange = (
		currentOption: ProductOptionFieldsFragment,
		updatedGroupSelection: RequiredPick<Partial<GroupSelection>, 'valid'>
	) => {
		const updateGroup = changeGroupValidity({ ...updatedGroupSelection, id: groupId }, currentOption, selections);
		if (updateGroup.valid && currentOption.value) {
			void trackChangeOption(group, currentOption);
		}

		change(currentOption, updateGroup);
	};

	const testId = kebabCase(`product-option-${option.name}`).toString();
	return isTextField ? (
		<>
			{/* breaking the textbox into its own line */}
			<div style={{ flexBasis: '100%' }} />
			<div
				data-testid={testId}
				className={`pa0 bg-transparent ${productGroupOption} w-100 mr2`}
				data-automation={generateProductOptionNameSelector(`product-option-button-${option.name}`)}>
				<OptionText
					{...option}
					testId="option-text"
					className="w-100"
					maxLength={60}
					params={optionSelectionFound?.params}
					change={onChange}
					value={optionSelectionFound ? optionSelectionFound.value : ''}
				/>
			</div>
		</>
	) : (
		<button
			className={`pa0 b--solid br2 ba bg-transparent pointer ${selectedClassName} ${className ?? ''}`.trim()}
			tabIndex={0}
			data-automation={generateProductOptionNameSelector(`product-option-button-${option.name}`)}
			data-testid={testId}
			data-analytics={
				group.type === 'VARIATION'
					? `product-variation-section~${kebabCase(group.label)}~${kebabCase(option.name)}~${groupPosition}`
					: undefined
			}
			{...(!isTextField ? { onClick } : {})}>
			<OptionPill testId="option-pill" className="w-100" showCheckmark={showCheckmark} {...option} />
		</button>
	);
};

/**
 * @typedef ProductOptionsProps
 */
export type ProductOptionsProps = {
	change: (option: ProductOptionFieldsFragment, group?: RequiredPick<Partial<GroupSelection>, 'id' | 'valid'>) => void;
	groupPosition: number;
	selections?: OptionSelection[];
	optionClassName?: string;
} & ProductOptionGroupFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Options Dropdown Component
 *
 * @param {ProductOptionsProps} props
 */
export const ProductOptionsDropdown: FunctionComponent<ProductOptionsProps> = ({
	className = '',
	optionClassName = 'w-100',
	selections = [],
	change,
	groupPosition,
	...group
}) => {
	const shouldCloseOnChoice = group.options.every((option) => option.type !== 'TEXTBOX');
	return (
		<div className={className} data-testid="product-options-dropdown" data-automation="product-options-dropdown">
			<DropdownMenu title={getSelectedLabel(`Select ${group.label}`, selections, group)} shouldCloseOnChoice={shouldCloseOnChoice}>
				{group.options.map((option, index) => (
					<ProductOption
						{...option}
						groupPosition={groupPosition}
						key={index}
						className={`bn ${optionClassName}`}
						showCheckmark={true}
						group={group}
						change={change}
						selections={selections}
						selected={isCurrentOptionSelected(option, selections)}
					/>
				))}
			</DropdownMenu>
		</div>
	);
};

/**
 * Product Options Drawer Component
 *
 * @param {ProductOptionsProps} props
 */
export const ProductOptionsDrawer: FunctionComponent<ProductOptionsProps> = ({
	className = '',
	optionClassName = 'w-100',
	selections = [],
	change,
	groupPosition,
	...group
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const keyName = kebabCase(group.label);
	const titleValue = `Select ${group.label}`;
	const drawerProps: Partial<DrawerProps> = { ariaLabel: titleValue, direction: 'right', showOverlay: false };
	const drawerTitle: JSX.Element = <div className="flex flex-row flex-nowrap items-center justify-start w-100 f4 pr4">{titleValue}</div>;
	const requiresConfirmation = Boolean(
		selections.find((optionSelection) => [OptionSelectionType.INDEPENDENT, OptionSelectionType.MULTIPLE].includes(optionSelection.type))
	);
	const toggle = () => setIsOpen(!isOpen);
	const confirm = () => setIsOpen(false);
	const onChange = (option: ProductOptionFieldsFragment, groupSelection?: RequiredPick<Partial<GroupSelection>, 'id' | 'valid'>) => {
		change(option, groupSelection);
		return option.type === 'RADIO' && !requiresConfirmation ? confirm() : void 0;
	};
	const confirmButton: JSX.Element = (
		<div className="w-100" key={`${keyName}-confirm`}>
			<StyledButton onClick={confirm}>Done</StyledButton>
		</div>
	);

	return (
		<div className={`${DROPDOWN_CLASSES} ${className}`.trim()} data-testid="product-options-drawer">
			<DropdownMenuSelect toggle={toggle} expanded={isOpen}>
				{getSelectedLabel(`Select ${group.label}`, selections, group)}
				<ChevronRightIcon className="flex-end ml2 f3 w2" />
			</DropdownMenuSelect>
			{isOpen && (
				<Drawer {...drawerProps} title={drawerTitle} key={keyName} customCloseButton={requiresConfirmation ? <></> : undefined}>
					{group.options
						.map((option, index) => (
							<ProductOption
								{...option}
								groupPosition={groupPosition}
								key={index}
								className={`bw1 mb2 ${optionClassName}`}
								group={group}
								change={onChange}
								showCheckmark={true}
								selections={selections}
								selected={isCurrentOptionSelected(option, selections)}
							/>
						))
						.concat(requiresConfirmation ? [confirmButton] : [])}
				</Drawer>
			)}
		</div>
	);
};

/**
 * Product Options List Component
 *
 * @param {ProductOptionsProps} props
 */
export const ProductOptionsList: FunctionComponent<ProductOptionsProps> = ({
	className,
	optionClassName = 'w5',
	selections = [],
	change,
	groupPosition,
	...group
}) => {
	return (
		<div className={`list flex flex-row flex-wrap items-center w-100 ${className}`.trim()} data-testid="product-options-list">
			{group.options.map((option, index) => (
				<ProductOption
					{...option}
					groupPosition={groupPosition}
					key={index}
					className={`bw1 mb2 mr2 ${optionClassName}`}
					group={group}
					change={change}
					selections={selections}
					selected={isCurrentOptionSelected(option, selections)}
				/>
			))}
		</div>
	);
};

/**
 * @typedef ProductFactoryOptionsListProps
 */
export type ProductFactoryOptionsListProps = {
	selections: OptionSelection[];
	change: (option: ProductOptionFieldsFragment, groupSelection?: RequiredPick<Partial<GroupSelection>, 'id' | 'valid'>) => void;
	groupPosition: number;
	optionClassName?: string;
} & ProductOptionGroupFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Factory Options List
 *
 * @param {ProductFactoryOptionsListProps} commonProps
 */
export const ProductFactoryOptionsList: FunctionComponent<ProductFactoryOptionsListProps> = (commonProps) => {
	const { isNotSmall } = useClientOnlyBreakpoints();
	return commonProps.options.length > 4 ? (
		isNotSmall ? (
			<ProductOptionsDropdown {...commonProps} className={`${productOption} ${commonProps.className}`.trim()} />
		) : (
			<ProductOptionsDrawer {...commonProps} className="w-100 mt2" />
		)
	) : isNotSmall ? (
		<ProductOptionsList {...commonProps} optionClassName={`${commonProps.optionClassName} ${productGroupOption}`} />
	) : (
		<ProductOptionsDrawer {...commonProps} className="w-100 mt2" />
	);
};

/**
 * ProductOptionsContentProps
 */
export type ProductOptionsContentProps = {
	selections: OptionSelection[];
	groupPosition: number;
	optionClassName?: string;
} & ProductOptionGroupFieldsFragment &
	Pick<ProductOptionSelection, 'update'> &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Options Component
 *
 * @param {ProductOptionsProps} props
 * @param {string} props.className
 * @param {ProductOptionSelection.update} props.update
 */
export const ProductOptionsContent: FunctionComponent<ProductOptionsContentProps> = ({
	className = '',
	optionClassName = `w-100`,
	update,
	selections,
	...group
}) => {
	const onOptionChange = (
		option: ProductOptionFieldsFragment,
		groupSelection?: RequiredPick<Partial<GroupSelection>, 'id' | 'valid'>
	): void => {
		const optionSelection = selections.find((currentOption) => currentOption.id === kebabCase(option.name));
		return update && optionSelection && update(changeOption(option, selections), optionSelection, groupSelection);
	};
	return <ProductFactoryOptionsList {...{ className, optionClassName, selections, change: onOptionChange, ...group }} />;
};
